/**
 * @module cercaliagl/control/Zoom
 */
import Control from './Control';
import MapControls from './MapControls';
import VZoom, { EventType } from '../view/VZoom';

/**
 * @typedef {Object} ZoomOptions
 * @property {string|undefined} className Class name
 * @property {number|undefined} delta Delta zoom
 * @property {number|undefined} duration Duration zoom
 * @property {string|undefined} position Positions available: `bottom-right`, `bottom-left`, `top-right`, `top-left`. Default:`bottom-right`
 * @property {Element|undefined} target Target element
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.Zoom();
 * ```
 * <br/>
 * Zoom control contains zoom buttons, rotate and a button to enable/disable 3D view.
 * @api
 */
class Zoom extends Control {
  /**
   * @param {ZoomOptions} [options] Zoom options.
   */
  constructor(options = {}) {
    const className = options.className !== undefined ? options.className : 'v-zoom';
    const delta = options.delta !== undefined ? options.delta : 0.25;
    const positionClass = options.position !== undefined ? options.position : 'bottom-right';
    const zoomView = new VZoom();
    const element = zoomView.getElement();

    element.className = `${className} ${positionClass}`;

    super({
      element,
      target: options.target,
      className: 'cercaliagl.control.Zoom',
      name: MapControls.ZOOM,
    });

    /**
     * @private
     * @type {VZoom}
     */
    this.zoomView_ = zoomView;

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {boolean}
     */
    this.d3enabled_ = false;

    /**
     * @type {number}
     * @private
     */
    this.duration_ = options.duration !== undefined ? options.duration : 250;

    this.zoomView_.on(EventType.ZOOM_IN, this.zoomByDelta_.bind(this, delta));
    this.zoomView_.on(EventType.ZOOM_OUT, this.zoomByDelta_.bind(this, -delta));
    this.zoomView_.on(EventType.GPS, this.handleGpsClick_.bind(this));
    this.zoomView_.on(EventType.D3, this.handleClickD3_.bind(this));
    this.zoomView_.on(EventType.ROTATE, this.handleRotateClick_.bind(this));
    this.zoomView_.on(EventType.ROTATE_LEFT, this.handleRotateDirectionClick_.bind(this, 'left'));
    this.zoomView_.on(EventType.ROTATE_RIGHT, this.handleRotateDirectionClick_.bind(this, 'right'));
  }

  /**
   * Initialize control once is created.
   */
  initialize() {
    const mbMap = this.getMap();
    const pitch = mbMap.getPitch();
    const cercaliaMap = this.getCercaliaMap();
    if (cercaliaMap.getGeolocation()) {
      this.zoomView_.show('gps');
    }
    if (pitch === 0) {
      this.zoomView_.set3D(false);
    }
    mbMap.on('rotate', () => this.zoomView_.rotateCompass(mbMap.getBearing() * -1));
    mbMap.on('pitch', this.handlePitch_.bind(this));
  }

  /**
   * @private
   */
  handleGpsClick_() {
    const cercaliaMap = this.getCercaliaMap();
    cercaliaMap.centerOnGeolocation();
  }

  /**
   * @private
   */
  handleClickD3_() {
    const mbMap = this.getMap();
    const pitch = mbMap.getPitch();
    if (pitch === 0) {
      mbMap.flyTo({ pitch: 30 });
    } else {
      mbMap.flyTo({ pitch: 0 });
    }
    this.handlePitch_();
  }

  /**
   * @private
   */
  handlePitch_() {
    const mbMap = this.getMap();
    const pitch = mbMap.getPitch();
    if (pitch === 0) {
      this.zoomView_.set3D(false);
    } else {
      this.zoomView_.set3D(true);
    }
  }

  /**
   * @private
   * @param {number} delta Zoom delta.
   */
  zoomByDelta_(delta) {
    const map = this.getCercaliaMap();
    const currentZoom = map.getZoom();
    if (currentZoom !== undefined) {
      const newZoom = currentZoom + delta;
      if (this.duration_ > 0) {
        this.map_.zoomTo(newZoom, {
          duration: this.duration_,
        });
      } else {
        this.map_.setZoom(newZoom);
      }
    }
  }

  /**
   * Set rotation to default. `0` degrees.
   * @private
   */
  handleRotateClick_() {
    const cercaliaMap = this.getCercaliaMap();
    cercaliaMap.setRotation(0);
    this.zoomView_.rotateCompass(0);
  }

  /**
   * @private
   * @param {string} direction Direction
   */
  handleRotateDirectionClick_(direction) {
    const cercaliaMap = this.getCercaliaMap();
    const rotation = cercaliaMap.getRotation() + (direction === 'left' ? -15 : 15);
    cercaliaMap.setRotation(rotation);
    this.zoomView_.rotateCompass(rotation);
  }
}

export default Zoom;
